import { supabase } from '../supabase';
import { checkRateLimit } from '../utils/rateLimiter';
import { checkAuthentication } from './auth';

/**
 * Get centralized game constants
 * @returns {Object} Game constants
 */
export function getGameConstants() {
  return {
    // Mining rig constants
    MAX_RIGS: 4, // Maximum number of mining rigs a player can own
    RIG_TIER_POWERS: [10, 15, 25, 40], // Mining power for each tier
    RIG_TIER_COSTS: [500, 1000, 2000, 4000],
    RIG_TIER_EFFICIENCY: [1, 1.2, 1.5, 2], // Efficiency multiplier for each tier
    
    // Mining reward constants
    DAILY_TOKEN_POOL: 2000, // Daily tokens distributed across all miners
    MAX_HOURLY_REWARD: 50, // Maximum tokens earned per hour
    
    // Time constants
    PASSIVE_INCOME_FREQUENCY: 1, // How often passive income is calculated (hours)
    MAX_OFFLINE_HOURS: 168, // Maximum hours of offline income (7 days)
    
    // Validation settings
    MONEY_TOLERANCE: 1, // Small tolerance for floating point errors
    
    // Economy configuration
    MIN_MONEY: 0, // Never allow negative money
    MINIMUM_WITHDRAWAL_AMOUNT: 1000, // Minimum $REDDICKS for withdrawal
    MIN_WALLET_BALANCE_FOR_MINING: 15000, // Minimum $REDDICKS in wallet to enable mining rewards
    
    // Other game limits
    MAX_SAVES_PER_USER: 1,
  };
}

/**
 * Get user's save game
 * @param {string} userId - User ID from Supabase
 * @returns {Promise<Object>} User's save game or error
 */
export async function getUserSave(userId) {
  if (!userId) return { error: 'No user ID provided' };
  
  // Get game constants from centralized function
  const CONSTANTS = getGameConstants();
  
  try {
    // Get user's save game
    const { data: save, error: fetchError } = await supabase
      .from('game_saves')
      .select('*')
      .eq('user_id', userId)
      .single();
      
    if (fetchError) {
      if (fetchError.code === 'PGRST116') {
        // No save found, return empty result
        return { save: null, constants: CONSTANTS };
      }
      console.error('Error fetching user save:', fetchError);
      return { error: fetchError };
    }
    
    // Process any pending mining income since last played
    if (save) {
      const now = new Date();
      const lastPlayed = new Date(save.last_played);
      const hoursElapsed = (now - lastPlayed) / (1000 * 60 * 60); // Convert to hours
      
      // If significant time has passed, process mining income
      if (hoursElapsed > 0.05) { // About 3 minutes minimum
        console.log(`Processing income for ${hoursElapsed.toFixed(2)} hours of mining`);
        const { save: updatedSave, income } = await processMiningIncome(save.id, hoursElapsed);
        
        if (updatedSave) {
          // Return the updated save with game constants and any income received
          return { 
            save: updatedSave, 
            constants: CONSTANTS,
            incomeUpdate: income > 0 ? { income, hoursElapsed } : null
          };
        }
      }
    }
    
    // Return the save with game constants
    return { save, constants: CONSTANTS };
  } catch (error) {
    console.error('Unexpected error fetching user save:', error);
    return { error };
  }
}

/**
 * Create a new save for a user
 * @param {string} userId - User ID
 * @param {string} companyName - Company name
 * @param {Object} initialData - Initial game data
 * @returns {Promise<Object>} Created save or error
 */
export async function createSave(userId, companyName, initialData = {}) {
  if (!userId) return { error: 'No user ID provided' };
  if (!companyName) return { error: 'No company name provided' };
  
  // Apply rate limiting
  const rateLimitResult = checkRateLimit(userId, 'save_creation');
  if (!rateLimitResult.allowed) {
    return { 
      error: `Rate limit exceeded. Please wait ${rateLimitResult.retryAfter} seconds before trying again.`,
      success: false,
      rateLimit: true,
      retryAfter: rateLimitResult.retryAfter
    };
  }
  
  // Get game constants from centralized function
  const CONSTANTS = getGameConstants();
  
  try {
    // Check if user already has a save
    const { data: existingSaves, error: fetchError } = await supabase
      .from('game_saves')
      .select('*')
      .eq('user_id', userId);
      
    if (fetchError) {
      console.error('Error checking for existing saves:', fetchError);
      return { error: fetchError };
    }
    
    if (existingSaves && existingSaves.length >= CONSTANTS.MAX_SAVES_PER_USER) {
      console.log('User already has a save, returning it:', existingSaves[0]);
      return { 
        error: 'User already has a save game',
        save: existingSaves[0],
        constants: CONSTANTS
      };
    }
    
    // Generate a unique save ID (8 characters)
    const saveId = Math.random().toString(36).substring(2, 10);
    
    // Set initial game data with default values
    const baseInitialData = {
      // Resources and stats
      money: 500,       // Starting money
      energy: 100,      // Max energy
      miningRigs: [],   // Start with no mining rigs
      miningPower: 0    // Start with 0 mining power
    };
    
    // Merge base data with any provided initialData
    const gameData = { ...baseInitialData, ...initialData };
    
    // Extract fields that should be stored separately
    const money = gameData.money || 500;
    const mining_power = gameData.miningPower || 0;
    
    // Remove from data object
    delete gameData.money;
    delete gameData.miningPower;
    
    // Create the save in the database
    const { data, error } = await supabase
      .from('game_saves')
      .insert([{
        save_id: saveId,
        user_id: userId,
        company_name: companyName,
        data: gameData,
        money: money,
        mining_power: mining_power,
        created_at: new Date(),
        last_played: new Date()
      }])
      .select()
      .single();
      
    if (error) {
      console.error('Error creating save:', error);
      return { error };
    }
    
    return { 
      success: true, 
      save: data,
      constants: CONSTANTS
    };
  } catch (error) {
    console.error('Unexpected error creating save:', error);
    return { error };
  }
}

/**
 * Update a game save
 * @param {string} saveId - Save ID (record id from Supabase)
 * @param {Object} gameData - Updated game data
 * @returns {Promise<Object>} Success indicator or error
 */
export async function updateSave(saveId, gameData) {
  if (!saveId) return { error: 'No save ID provided' };

  if (!gameData) return { error: 'No game data provided' };
  
  // Get game constants from centralized function
  const CONSTANTS = getGameConstants();
  
  try {
    // Get current save data
    const { data: currentSave, error: fetchError } = await supabase
      .from('game_saves')
      .select('*')
      .eq('id', saveId)
      .single();
      
    if (fetchError) {
      console.error('Error fetching save for update:', fetchError);
      return { error: fetchError };
    }
    
    // Verify the save belongs to an authenticated user
    const { authenticated } = await checkAuthentication(currentSave.user_id);
    if (!authenticated) {
      return { error: 'User not authenticated', success: false };
    }
    
    // Prepare new data
    let newData = { ...currentSave.data };
    
    // If gameData contains a data field, use that
    if (gameData.data) {
      newData = { ...newData, ...gameData.data };
    } else {
      // Otherwise, assume gameData is the data object itself
      newData = { ...newData, ...gameData };
    }
    
    // Extract fields that should be stored separately
    let updateValues = {
      data: newData,
      last_played: new Date()
    };
    
    // Handle money in its own field
    if (typeof gameData.money === 'number') {
      updateValues.money = gameData.money;
    } else if (typeof newData.money === 'number') {
      updateValues.money = newData.money;
      delete newData.money; // Remove from data object
    }
    
    // Handle mining_power in its own field
    if (typeof gameData.mining_power === 'number') {
      updateValues.mining_power = gameData.mining_power;
    } else if (typeof newData.miningPower === 'number') {
      updateValues.mining_power = newData.miningPower;
      delete newData.miningPower; // Remove from data object
    }
    
    // Update the save
    const { data, error } = await supabase
      .from('game_saves')
      .update(updateValues)
      .eq('id', saveId)
      .select()
      .single();
      
    if (error) {
      console.error('Error updating game data:', error);
      return { error };
    }
    
    return { 
      success: true, 
      save: data,
      constants: CONSTANTS
    };
  } catch (error) {
    console.error('Unexpected error updating game data:', error);
    return { error };
  }
}

/**
 * Calculate mining rig income based on mining power
 * @param {string} saveId - Save ID (record id from Supabase)
 * @param {number} hoursElapsed - Hours since last update
 * @returns {Promise<Object>} Updated save or error
 */
export async function processMiningIncome(saveId, hoursElapsed = 0) {
  if (!saveId) return { error: 'No save ID provided' };
  if (typeof hoursElapsed !== 'number' || hoursElapsed < 0) {
    return { error: 'Invalid hours elapsed value' };
  }
  
  // Get game constants from centralized function
  const CONSTANTS = getGameConstants();
  
  // Cap maximum hours for passive income
  if (hoursElapsed > CONSTANTS.MAX_OFFLINE_HOURS) {
    hoursElapsed = CONSTANTS.MAX_OFFLINE_HOURS;
  }
  
  try {
    // Get the save
    const { data: currentSave, error: fetchError } = await supabase
      .from('game_saves')
      .select('*')
      .eq('id', saveId)
      .single();
      
    if (fetchError) {
      console.error('Error fetching save for income processing:', fetchError);
      return { error: fetchError };
    }
    
    // Verify the save belongs to an authenticated user
    const { authenticated } = await checkAuthentication(currentSave.user_id);
    if (!authenticated) {
      return { error: 'User not authenticated', success: false };
    }
    
    // Get the mining rigs and mining power
    const rigs = Array.isArray(currentSave.data.miningRigs) ? currentSave.data.miningRigs : [];
    let miningPower = currentSave.data.miningPower || 0;
    
    // If mining power wasn't calculated yet, calculate it now
    if (miningPower === 0 && rigs.length > 0) {
      miningPower = rigs.reduce((total, rig) => {
        if (rig.isActive) {
          return total + CONSTANTS.RIG_TIER_POWERS[rig.tier - 1];
        }
        return total;
      }, 0);
    }
    
    // Calculate mining rewards based on power
    // For simplicity, we're using a fixed total network power value
    const totalNetworkPower = 1000;
    const dailyPoolShare = (miningPower / totalNetworkPower) * CONSTANTS.DAILY_TOKEN_POOL;
    
    // Calculate hourly rate and cap it at the maximum allowed
    const hourlyRate = Math.min(dailyPoolShare / 24, CONSTANTS.MAX_HOURLY_REWARD);
    const income = Math.floor(hourlyRate * hoursElapsed);
    
    if (income > 0) {
      // Update rig total mined values
      const updatedRigs = rigs.map(rig => {
        if (rig.isActive) {
          const rigPower = CONSTANTS.RIG_TIER_POWERS[rig.tier - 1];
          const rigShare = miningPower > 0 ? (rigPower / miningPower) : 0;
          const rigIncome = income * rigShare;
          
          return {
            ...rig,
            totalMined: (rig.totalMined || 0) + rigIncome
          };
        }
        return rig;
      });
      
      const updatedData = {
        ...currentSave.data,
        miningRigs: updatedRigs,
        energy: 100 // Always keep energy at 100 (unlimited)
      };
      
      const { data, error } = await supabase
        .from('game_saves')
        .update({ 
          data: updatedData,
          money: (currentSave.money || 0) + income,
          mining_power: miningPower,
          last_played: new Date()
        })
        .eq('id', saveId)
        .select()
        .single();
        
      if (error) {
        console.error('Error processing passive income:', error);
        return { error };
      }
      
      return { 
        success: true, 
        save: data, 
        income, 
        miningPower,
        hourlyRate,
        message: `You earned ${income} REDDICKS while away! (${hourlyRate} REDDICKS/hr)`,
        constants: CONSTANTS
      };
    }
    
    return { 
      success: true, 
      save: currentSave, 
      income: 0,
      miningPower,
      hourlyRate,
      constants: CONSTANTS 
    };
  } catch (error) {
    console.error('Unexpected error processing income:', error);
    return { error };
  }
}

/**
 * Update mining rigs and money
 * @param {string} saveId - Save ID (record id from Supabase)
 * @param {Array} rigs - Array of mining rig objects
 * @param {number} money - New money value
 * @param {number} miningPower - Total mining power
 * @returns {Promise<Object>} Success indicator or error
 */
export async function updateMiningRigs(saveId, rigs, money, miningPower) {
  if (!saveId) return { error: 'No save ID provided' };
  if (!Array.isArray(rigs)) return { error: 'Invalid rig data format' };
  if (typeof money !== 'number') return { error: 'Invalid money value' };
  if (money < 0) return { error: 'Money cannot be negative' };
  
  // Apply rate limiting
  const rateLimitResult = checkRateLimit(saveId, 'mining_rig_purchase');
  if (!rateLimitResult.allowed) {
    return { 
      error: `Rate limit exceeded. Please wait ${rateLimitResult.retryAfter} seconds before trying again.`,
      success: false,
      rateLimit: true,
      retryAfter: rateLimitResult.retryAfter
    };
  }
  
  // Get game constants from centralized function
  const CONSTANTS = getGameConstants();
  
  try {
    // First get the current save data
    const { data: currentSave, error: fetchError } = await supabase
      .from('game_saves')
      .select('data, user_id, mining_power, money')
      .eq('id', saveId)
      .single();
      
    if (fetchError) {
      console.error('Error fetching save for mining update:', fetchError);
      return { error: fetchError };
    }
    
    // Verify the save belongs to an authenticated user
    const { authenticated } = await checkAuthentication(currentSave.user_id);
    if (!authenticated) {
      return { error: 'User not authenticated', success: false };
    }
    
    // Get current values from the database
    const currentRigs = Array.isArray(currentSave.data.miningRigs) ? currentSave.data.miningRigs : [];
    const currentMoney = currentSave.money || 0;
    
    // Enhanced validation logic
    
    // 1. Validate rig purchase constraints
    // Can only add one rig at a time
    if (rigs.length !== currentRigs.length + 1) {
      return { error: 'Invalid rig count change', success: false };
    }
    
    // Cannot exceed max rigs
    if (rigs.length > CONSTANTS.MAX_RIGS) {
      return { error: 'Maximum rig count exceeded', success: false };
    }
    
    // Get the new rig (the one just added)
    const newRig = rigs[rigs.length - 1];
    if (!newRig || typeof newRig.tier !== 'number' || newRig.tier < 1 || newRig.tier > CONSTANTS.MAX_RIGS) {
      return { error: 'Invalid rig tier', success: false };
    }
    
    // Check if this tier is already owned
    if (currentRigs.some(rig => rig.tier === newRig.tier)) {
      return { error: 'This rig tier is already owned', success: false };
    }
    
    // 2. Calculate and validate costs
    const tierIndex = newRig.tier - 1;
    const expectedCost = CONSTANTS.RIG_TIER_COSTS[tierIndex];
    const expectedMoney = currentMoney - expectedCost;
    
    // Verify user has enough money
    if (currentMoney < expectedCost) {
      return { error: 'Insufficient funds', success: false };
    }
    
    // Verify money calculation is correct (with tolerance for floating point errors)
    if (Math.abs(money - expectedMoney) > CONSTANTS.MONEY_TOLERANCE) {
      return { 
        error: 'Invalid money calculation', 
        success: false,
        expectedMoney: expectedMoney,
        providedMoney: money 
      };
    }
    
    // Validate mining power calculation
    const calculatedPower = rigs.reduce((total, rig) => {
      if (rig.isActive) {
        return total + CONSTANTS.RIG_TIER_POWERS[rig.tier - 1];
      }
      return total;
    }, 0);
    
    if (typeof miningPower !== 'number' || calculatedPower !== miningPower) {
      miningPower = calculatedPower; // Use server-calculated value
    }
    
    // 3. Ensure transaction atomicity (all or nothing)
    // All validation passed, update the data
    const updatedData = {
      ...currentSave.data,
      miningRigs: rigs,
      energy: 100 // Always keep energy at 100 (unlimited)
    };
    
    // Save it back with money and mining_power in their own fields
    const { data, error } = await supabase
      .from('game_saves')
      .update({ 
        data: updatedData,
        money: expectedMoney,
        mining_power: miningPower,
        last_played: new Date()
      })
      .eq('id', saveId)
      .select()
      .single();
      
    if (error) {
      console.error('Error updating mining rigs:', error);
      return { error };
    }
    
    return { 
      success: true, 
      save: data,
      constants: CONSTANTS
    };
  } catch (error) {
    console.error('Unexpected error updating mining rigs:', error);
    return { error };
  }
}

/**
 * Update a single mining rig's active status
 * @param {string} saveId - Save ID (record id from Supabase)
 * @param {Array} rigs - Updated array of mining rigs
 * @returns {Promise<Object>} Success indicator or error
 */
export async function updateRigStatus(saveId, rigs) {
  if (!saveId) return { error: 'No save ID provided' };
  if (!Array.isArray(rigs)) return { error: 'Invalid rig data format' };
  
  // Get game constants from centralized function
  const CONSTANTS = getGameConstants();
  
  try {
    // First get the current save data
    const { data: currentSave, error: fetchError } = await supabase
      .from('game_saves')
      .select('data, user_id, mining_power')
      .eq('id', saveId)
      .single();
      
    if (fetchError) {
      console.error('Error fetching save for rig status update:', fetchError);
      return { error: fetchError };
    }
    
    // Verify the save belongs to an authenticated user
    const { authenticated } = await checkAuthentication(currentSave.user_id);
    if (!authenticated) {
      return { error: 'User not authenticated', success: false };
    }
    
    // Get current rigs
    const currentRigs = Array.isArray(currentSave.data.miningRigs) ? currentSave.data.miningRigs : [];
    
    // Validate the rigs array
    if (rigs.length !== currentRigs.length) {
      return { error: 'Rig count mismatch', success: false };
    }
    
    // Calculate new mining power based on active rigs
    const newMiningPower = rigs.reduce((total, rig) => {
      if (rig.isActive) {
        return total + CONSTANTS.RIG_TIER_POWERS[rig.tier - 1];
      }
      return total;
    }, 0);
    
    // Update the save with new rigs and mining power
    const updatedData = {
      ...currentSave.data,
      miningRigs: rigs,
      energy: 100 // Always keep energy at 100 (unlimited)
    };
    
    // Save it back with mining_power in its own field
    const { data, error } = await supabase
      .from('game_saves')
      .update({ 
        data: updatedData,
        mining_power: newMiningPower,
        last_played: new Date()
      })
      .eq('id', saveId)
      .select()
      .single();
      
    if (error) {
      console.error('Error updating rig status:', error);
      return { error };
    }
    
    return { 
      success: true, 
      save: data,
      constants: CONSTANTS
    };
  } catch (error) {
    console.error('Unexpected error updating rig status:', error);
    return { error };
  }
}

/**
 * Record a withdrawal after a successful transaction
 * Sets the user's in-game money balance to 0.
 * @param {string} saveId - Save ID (record id from Supabase)
 * @param {number} withdrawnAmount - The amount withdrawn (for verification/logging)
 * @param {string} txId - Optional: The Radix transaction ID for verification
 * @returns {Promise<Object>} Success indicator or error
 */
export async function recordWithdrawal(saveId, withdrawnAmount, txId = null) {
  if (!saveId) return { error: 'No save ID provided' };
  if (typeof withdrawnAmount !== 'number' || withdrawnAmount <= 0) {
    return { error: 'Invalid withdrawal amount' };
  }
  
  try {
    // Get current save data
    const { data: currentSave, error: fetchError } = await supabase
      .from('game_saves')
      .select('*')
      .eq('id', saveId)
      .single();
      
    if (fetchError) {
      console.error('Error fetching save for withdrawal:', fetchError);
      return { error: fetchError };
    }
    
    // Verify the save belongs to an authenticated user
    const { authenticated } = await checkAuthentication(currentSave.user_id);
    if (!authenticated) {
      return { error: 'User not authenticated', success: false };
    }
    
    // Verify user has enough balance
    const currentBalance = currentSave.money || 0;
    if (currentBalance < withdrawnAmount) {
      return { error: 'Insufficient balance', success: false };
    }
    
    // Update the save with new balance
    const { data, error } = await supabase
      .from('game_saves')
      .update({ 
        money: 0, // Reset balance to 0 after withdrawal
        last_played: new Date()
      })
      .eq('id', saveId)
      .select()
      .single();
      
    if (error) {
      console.error('Error recording withdrawal:', error);
      return { error };
    }
    
    // Record the withdrawal transaction if txId is provided
    if (txId) {
      const { error: txError } = await supabase
        .from('withdrawals')
        .insert([{
          save_id: saveId,
          amount: withdrawnAmount,
          tx_id: txId,
          timestamp: new Date()
        }]);
        
      if (txError) {
        console.error('Error recording withdrawal transaction:', txError);
        // Don't fail the whole operation if just the transaction record fails
      }
    }
    
    return { 
      success: true, 
      save: data,
      withdrawnAmount,
      txId
    };
  } catch (error) {
    console.error('Unexpected error recording withdrawal:', error);
    return { error };
  }
}

/**
 * Get configuration value from the database
 * @param {string} key - Configuration key to fetch
 * @returns {Promise<Object>} Configuration value or error
 */
export async function getConfigValue(key) {
  try {
    const { data, error } = await supabase
      .from('config')
      .select('value')
      .eq('key', key);
      
    if (error) {
      console.error(`Error fetching config value for key ${key}:`, error);
      return { error };
    }
    
    // Check if we got any results
    if (!data || data.length === 0) {
      console.warn(`Config key '${key}' not found in database`);
      return { value: null, notFound: true };
    }
    
    return { value: data[0]?.value };
  } catch (error) {
    console.error(`Unexpected error fetching config value for key ${key}:`, error);
    return { error };
  }
}

